import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {Tooltip} from '@mui/material';
import {makeStyles} from '@mui/styles'
import clsx from 'clsx';
import SMCIcon from '../SMCIcon';
import {useSMCDisabledContext} from '../SMCDisabledContext';
import {hasValue} from '../../../utils/utils';

/**
 * @param {Object} props
 * @param {('ghost'|'filled'|'default')} [props.variant]
 * @param {boolean} [props.disabled]
 * @param {('filter'|'delete'|'new'|'refresh'|'revert'|'save'|'clear'|'calendar'|'plus')} [props.icon]
 * @param {number} [props.height]
 * @param {string} [props.className]
 * @param {boolean} [props.ignoreDisabledContext]
 * @param {node} [props.tooltip]
 * @param {('bottom-end'|'bottom-start'|'bottom'|'left-end'|'left-start'|'left'|'right-end'|'right-start'|'right'|'top-end'|'top-start'|'top')} [props.tooltipPlacement]
 * @param {('start'|'end')} [params.iconPosition]
 * @param {string} [params.labelClassName]
 */
function SMCButton({
    variant,
    disabled,
    children,
    icon,
    height,
    className,
    ignoreDisabledContext,
    tooltip,
    tooltipPlacement="top",
    textStyle,
    iconPosition="start",
    labelClassName,
    ...rest
}) {
    const disabledContext = useSMCDisabledContext();
    const _disabled = useMemo(() => {
        if (disabledContext && !ignoreDisabledContext) {
            if (disabledContext.forceDisable) {
                return true;
            }
            if (disabledContext.forceEnable) {
                return false;
            }
        }
        if (hasValue(disabled)) {
            return disabled;
        }
        return undefined;
    }, [disabled, disabledContext, ignoreDisabledContext]);

    const classes = useStyles({
        height
    });
    const buttonRootClass = (variant === 'ghost') ? classes.ghostVariant : classes.filledVariant;

    const button = (
        <button {...rest} disabled={_disabled} className={clsx(classes.root, buttonRootClass, className)}>
            <span className={clsx(classes.content,iconPosition==="start" ? null : classes.reverseContent,labelClassName)} style={textStyle} >
                {icon && <SMCIcon icon={icon} className={classes.icon} />}
                {children}
            </span>
        </button>
    );

    return (
        // If tooltip exists, wrap button with tooltip, otherwise return button
        tooltip ? (
            <Tooltip placement={tooltipPlacement} title={tooltip}>
                { button }
            </Tooltip>
        ) : (
            button
        )
    )
}

const useStyles = makeStyles({
    root: {
        alignItems:'center',
        overflow: 'visible',
        cursor: 'pointer',
        borderRadius: 0,
        '&:disabled': {
            opacity: '0.4',
            cursor: 'default'
        },
        verticalAlign: 'top'
    },
    ghostVariant: {
        height: props => props.height || '22px',
        background: 'transparent',
        border: '1px transparent solid',
        padding: '0 2px',
        margin: '0 2px',
        boxShadow: '0px 0px #F1F1F1',
        '&:not(:disabled)': {
            '&:hover': {
                borderColor: '#BEC7D6'
            },
            '&:active': {
                borderColor: '#A0A8B5',
            }
        }
    },
    filledVariant: {
        height: props => props.height || '30px',
        border: '1px solid #d2d5d9',
        padding: '0 10px',
        marginLeft: '5px',
        '&:not(:disabled)': {
            '&:hover': {
                borderColor: '#4fb4f1;'
            },
            '&:active': {
                backgroundColor: '#cccdcf'
            }
        }
    },
    content: {
        fontSize: '13px',
        color: '#304461',
        position: 'relative',
        overflow: 'hidden',
        padding: 'none',
        width: '100%',
        display: 'flex',
        alignItems: 'inherit',
        justifyContent: 'inherit',
    },
    icon: {
        marginRight: '5px'
    },
    reverseContent:{
        flexDirection: 'row-reverse',
        gridGap: '5px'
    }
})

SMCButton.propTypes = {
    variant: PropTypes.oneOf(['ghost', 'filled', 'default']),
    disabled: PropTypes.bool,
    children: PropTypes.node,
    icon: PropTypes.oneOf(['filter', 'delete', 'new', 'refresh', 'revert', 'save', 'clear', 'calendar', 'plus','edit','downArrow','filterWithBadge']),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    className: PropTypes.string,
    ignoreDisabledContext: PropTypes.bool,
    tooltip: PropTypes.string,
    tooltipPlacement: PropTypes.oneOf(['bottom-end', 'bottom-start', 'bottom', 'left-end', 'left-start', 'left', 'right-end', 'right-start', 'right', 'top-end', 'top-start', 'top']),
    iconPosition: PropTypes.oneOf(['start', 'end']),
    labelClassName:PropTypes.string
}

SMCButton.whyDidYouRender = true;

export default React.memo(SMCButton);